import { Button, Modal } from "@bcc-ui/ui-components";
import * as React from "react";
import { useTranslation } from "react-i18next";

import style from "./file-sharing.scss";
import { computePath, FOLDER_PLACEHOLDER_FILENAME, isValidFolderPath } from "./fileTreeUtils";
import FolderPathSelector from "./FolderPathSelector";
import { useAbortControllers } from "./useAbortControllers";
import { fileSharingService, generateChecksumSha256 } from "services/file-sharing/FileSharingService";
import { logger } from "utils/logging";

import testIds from "testIds.json";

const POST_UPLOAD_REFRESH_DELAY_MS = 1_500;

interface Props {
    isOpen: boolean;
    existingPaths: string[];
    onClose: () => void;
    onCreated: () => void;
}

const CreateFolderModal = (props: Props) => {
    const { t } = useTranslation();
    const abortControllers = useAbortControllers();

    const [selectedPath, setSelectedPath] = React.useState("");
    const [newFolder, setNewFolder] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | undefined>();

    React.useEffect(() => {
        if (props.isOpen) {
            setSelectedPath("");
            setNewFolder("");
            setLoading(false);
            setError(undefined);
        }
    }, [props.isOpen]);

    const handleCreate = async () => {
        if (!isValidFolderPath(newFolder)) {
            setError(t("FileSharing.createFolderModal.errorInvalidPath"));
            return;
        }

        const folderPath = computePath(selectedPath, newFolder);
        if (!folderPath) {
            setError(t("FileSharing.createFolderModal.errorEmptyPath"));
            return;
        }

        const abortController = new AbortController();
        abortControllers.push(abortController);
        setLoading(true);
        setError(undefined);

        try {
            const filename = `${folderPath}/${FOLDER_PLACEHOLDER_FILENAME}`;
            const buffer = new Uint8Array([0]).buffer;
            const checksum = await generateChecksumSha256(buffer);
            const { presigned_url } = await fileSharingService.initiateUpload(
                { filename, file_size: 1, checksum },
                abortController
            );
            await fileSharingService.uploadToS3(presigned_url, buffer, abortController.signal);
            await new Promise((resolve) => setTimeout(resolve, POST_UPLOAD_REFRESH_DELAY_MS));
            props.onCreated();
            props.onClose();
        } catch (err) {
            if (!abortController.signal.aborted) {
                logger.error("Failed to create folder", err instanceof Error ? err : new Error(String(err)));
                setError(t("FileSharing.createFolderModal.errorGeneric"));
            }
        } finally {
            if (!abortController.signal.aborted) {
                setLoading(false);
            }
        }
    };

    return (
        <Modal
            isOpen={props.isOpen}
            hideModal={props.onClose}
            title={t("FileSharing.createFolderModal.title")}
            className={style.dialog}
            buttons={
                <>
                    <Button
                        variant="Secondary"
                        onClick={props.onClose}
                        disabled={loading}
                        testId={testIds.common.dialog.closeButton}
                    >
                        {t("Common.cancel")}
                    </Button>
                    <Button
                        variant="Primary"
                        isLoading={loading}
                        onClick={() => void handleCreate()}
                        testId={testIds.common.dialog.primaryButton}
                    >
                        {t("FileSharing.createFolderModal.confirm")}
                    </Button>
                </>
            }
        >
            <div className={style.locationSection}>
                <FolderPathSelector
                    existingPaths={props.existingPaths}
                    selectedPath={selectedPath}
                    newFolder={newFolder}
                    pathLabel={t("FileSharing.createFolderModal.pathLabel")}
                    newFolderLabel={t("FileSharing.createFolderModal.newFolderLabel")}
                    newFolderPlaceholder={t("FileSharing.createFolderModal.newFolderPlaceholder")}
                    pathSelectId="create-folder-path-select"
                    newFolderInputId="create-folder-new-folder"
                    onSelectedPathChange={(path) => {
                        setSelectedPath(path);
                        setError(undefined);
                    }}
                    onNewFolderChange={(folder) => {
                        setNewFolder(folder);
                        setError(undefined);
                    }}
                />
            </div>
            {error && <div className={style.errorMessage}>{error}</div>}
        </Modal>
    );
};

export default CreateFolderModal;
